/**
 * The M2 bridge's per-system mining and salvage world (M3 plan §7 item 6, slice R22i). M2 keeps one rocks world and
 * one salvage world for the whole career, and the Reach switches systems under them. On every switch (the `m2-world`
 * enter hook, which runs after the session cleared the old system and before it streams the new one) the world of
 * the system left goes into the Reach's per-system deltas and the entered system's comes back:
 *
 * - **Rocks:** fractured parents become removed rocks and their fragments and chunks are dropped (they would sit at
 *   the same coordinates in the next system); M2's partly mined and removed rocks of the old field are written as
 *   remaining fractions by key into `state.deltas[old].rocks`. M2's removed set and tonnes stay keyed by the stable
 *   rock ids (`rockIdOf(key)`), so they need no import on entry.
 * - **Wrecks:** `space.salvage.exportDeltas()` goes into `state.deltas[old].wrecks` (M2's `WreckRecord`s, opaque to
 *   M3), and the entered system's records are imported (none: an empty world). The wreck key sequence stays the
 *   career's (keys never repeat).
 * - **Derelict:** M2's derelict sections (the salvage site) move onto the entered system's derelict POI
 *   (`salvageSiteOf`: the Harrow at home, else the system's first derelict POI; none without one) on every entry,
 *   after the old system's wrecks were taken and before the entered system's come back. The hull and collider are
 *   the Reach's (`buildDerelict(poi.seed)` through `provideDerelictCollider`); M2 adds only the cuttable sections.
 *   The salvage world is told the entered system's galaxy region first (`setRegion`, economy plan SY-4b), so the
 *   derelict's parts roll their Mk for it: the region comes from the galaxy data, never a random draw. The site
 *   also names what it is to the Rush-pattern roll (M8 MC-h1, `DerelictSite`): `sealed` for the Harrow and for a
 *   derelict in Okafor's lab system (story sites never carry the tag), `graveyard` in a derelict-graveyard system,
 *   else `plain`; again from the galaxy data, never a draw.
 * - **Graveyard sites (M8 MC-h2):** a derelict-graveyard system also makes up to `salvage.json graveyardSites.extraSites`
 *   more of its derelict POIs cuttable (`graveyardSalvageSites`), each with its own seed and the `graveyard` Rush
 *   site. They skip the story sites (the Harrow, Okafor's lab, Column Nine: none in a sealed system, and clear of a
 *   story site by `storyClearM` beyond its radius), every restorable derelict (M7 restarts those) and its docking
 *   volume (`dockClearM`), and sit `minSpacingM` apart. The salvage site itself is chosen exactly as before, so a
 *   derelict M7 restarts never moves. Pure: the layout, the galaxy seed and the story overlay, never a random draw.
 * - **First entry of a career** (a new game or a load: `ReachState.deltas` is a new object) and a re-entry of the
 *   same system change nothing: M2's own load already placed the live world, and the Reach state has no world of the
 *   system left to take. M2's save keeps the live world under `worlds[home id]` (D-TBD 50's open point: the career
 *   save key is the home id; the other systems' worlds ride in the save's `reach` block).
 * - **Seeded hulks (M9 CH-2c, plan `docs/plans/2026-10-05-m9-claimable-hulls.md`):** after every entry's switch, the
 *   entered system's seeded First Rush hulks (`hulks/place.ts hulksOf`) minus the career's finished ones
 *   (`career.hulks.done`), capped at `salvage.json hulks.maxLocal` unclaimed (an open claim's always comes, PG-2), spawn as `hulk` wrecks (`systemHulks`,
 *   `spawnSystemHulks`). Hulks are never saved: a switch's `importDeltas` drops the old system's, and every entry
 *   re-derives the new system's (a re-entry of the same system finds them alive: `spawnHulk` is idempotent by key).
 *   Pure placement from the galaxy seed and the story overlay, never a random draw.
 *
 * `watchSystemWorld` runs every tick while no switch runs (reference copies only, allocation-free, D-014).
 */
import { z } from 'zod'
import type { Quat, Vec3 } from '../../core/math'
import { logger } from '../../core/logger'
import type { M2Data } from '../../data/m2Schemas'
import hullsJson from '../../data/strategic/hulls.json'
import type { SystemLayout } from '../../galaxy/layout/types'
import type { Galaxy, RegionId } from '../../galaxy/types'
import { hulksConfig, type HulksConfig } from '../../hulks/data'
import { hulksOf, type HulkSpot } from '../../hulks/place'
import { ensureDelta } from '../../reach/state/deltas'
import type { ReachState } from '../../reach/types'
import type { DerelictSite } from '../../salvage/generate'
import { wreckRecordSchema } from '../../save/record'
import type { DerelictSpot, FieldMixAt, RocksRuntime, SalvageRuntime, WreckRecord } from '../../space/contracts'
import { storyData } from '../../story/data'
import { frStationOf } from '../../story/reactivation/eligibility'
import type { ReactivationConfig } from '../../story/schemas'
import { sitesConfig, type StorySites } from '../../story-link/sites'
import type { DerelictPose, FieldLayout } from '../../world/field'
import { leavingRockDeltas, writeRockFractions } from './rocks'

/** The enter hook's name. */
export const SYSTEM_WORLD_HOOK = 'm2-world'

const NO_KEYS: readonly string[] = Object.freeze([])
const NO_SITES: readonly SalvageSite[] = Object.freeze([])

/** The system M2's world last ran in, with its field and rock keys (kept by `watchSystemWorld`). */
export interface SystemWorldWatch {
  systemId: string | null
  field: FieldLayout | null
  keys: readonly string[]
  /** The `ReachState.deltas` seen with it: a new object means a new game or a load replaced the Reach state. */
  deltas: ReachState['deltas'] | null
  /** The live system's field mix lookup (the bridgeMining feature sets it on entry): grades the rock fractions on exit (MC-a6). */
  mixAt: FieldMixAt | null
}

export function createSystemWorldWatch(): SystemWorldWatch {
  return { systemId: null, field: null, keys: NO_KEYS, deltas: null, mixAt: null }
}

/** Records the live system, its merged field and keys (parallel) and the state's deltas. Allocation-free. */
export function watchSystemWorld(
  watch: SystemWorldWatch,
  systemId: string,
  field: FieldLayout | null,
  keys: readonly string[],
  state: ReachState,
): void {
  watch.systemId = systemId
  watch.field = field
  watch.keys = keys
  watch.deltas = state.deltas
}

/** What a switch writes: M2's mining config and its space world's rocks and salvage. */
export interface SystemWorldTarget {
  readonly data: { readonly mining: Pick<M2Data['mining'], 'massPerCubicMetre' | 'mix'> }
  readonly space: {
    readonly rocks: Pick<RocksRuntime, 'exportDeltas' | 'importDeltas'>
    readonly salvage: Pick<SalvageRuntime, 'exportDeltas' | 'importDeltas' | 'setDerelict' | 'setRegion'>
  }
}

const wrecksSchema = z.array(wreckRecordSchema)

/** The stored wreck records of `systemId` (validated; a bad block is dropped with a warning). */
export function storedWrecks(state: ReachState, systemId: string): WreckRecord[] {
  const stored = state.deltas[systemId]?.wrecks
  if (stored === undefined || stored.length === 0) return []
  const parsed = wrecksSchema.safeParse(stored)
  if (parsed.success) return parsed.data
  logger.warn(`bridge: the stored wrecks of ${systemId} are invalid and are dropped: ${parsed.error.issues[0]?.message ?? ''}`)
  return []
}

/** A system's salvage site: M2's derelict sections at a derelict POI's pose, with parts from its seed. */
export interface SalvageSite {
  readonly poiId: string
  readonly pose: DerelictPose
  readonly seed: number
  /** The system's galaxy region (the derelict's parts roll their Mk for it; economy plan SY-4b). */
  readonly region: RegionId
  /** What the site is to the Rush-pattern roll (M8 MC-h1): sealed (the Harrow, Okafor's lab system), graveyard or plain. */
  readonly rush: DerelictSite
}

/**
 * The salvage site of a system: the home cluster's Harrow, else its first derelict POI, else none. Its Rush site
 * (M8 MC-h1) is `sealed` for the Harrow and in the system of Okafor's lab (`story/sites.json okaforLab.anchor`), so
 * no story derelict is ever tagged; `graveyard` in a derelict-graveyard system; else `plain`.
 */
export function salvageSiteOf(
  layout: Pick<SystemLayout, 'pois' | 'home' | 'region'> & Partial<Pick<SystemLayout, 'archetype' | 'anchor'>>,
): SalvageSite | null {
  const home = layout.home
  const harrow = home === null ? undefined : layout.pois.find((p) => p.id === home.harrowPoiId)
  const poi = harrow ?? layout.pois.find((p) => p.kind === 'derelict')
  if (poi === undefined) return null
  const [px, py, pz] = poi.position
  const [qx, qy, qz, qw] = poi.rotation
  const anchor = layout.anchor ?? null
  const sealed = harrow !== undefined || (anchor !== null && anchor === sitesConfig().okaforLab.anchor)
  const rush: DerelictSite = sealed ? 'sealed' : layout.archetype === 'derelict-graveyard' ? 'graveyard' : 'plain'
  return {
    poiId: poi.id,
    pose: { position: { x: px, y: py, z: pz }, rotation: { x: qx, y: qy, z: qz, w: qw } },
    seed: poi.seed,
    region: layout.region,
    rush,
  }
}

/** `salvage.json graveyardSites` (M8 MC-h2). */
export type GraveyardSitesConfig = M2Data['salvage']['graveyardSites']

/** What the graveyard sites read besides the layout: the galaxy seed (restorable rolls) and the story overlay. */
export interface GraveyardSiteContext {
  readonly galaxySeed: number
  /** The M7 story sites (`story-link/sites.ts storySites`); null when unknown, and then no extra site is made. */
  readonly story: Pick<StorySites, 'columnNine' | 'okaforLab'> | null
}

type GraveyardLayout = Pick<SystemLayout, 'systemId' | 'systemIndex' | 'region' | 'archetype' | 'pois' | 'home'>
type PoiOf = SystemLayout['pois'][number]

function distance(a: readonly number[], b: readonly number[]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
}

function siteAt(poi: PoiOf, region: RegionId, rush: DerelictSite): SalvageSite {
  const [px, py, pz] = poi.position
  const [qx, qy, qz, qw] = poi.rotation
  return { poiId: poi.id, pose: { position: { x: px, y: py, z: pz }, rotation: { x: qx, y: qy, z: qz, w: qw } }, seed: poi.seed, region, rush }
}

/**
 * The extra salvage sites of a derelict-graveyard system (M8 MC-h2): its other derelict POIs, in layout order, up to
 * `cfg.extraSites`. None outside a graveyard system, without a salvage site, at a sealed site (the Harrow, Okafor's
 * lab system) or without the story overlay. A candidate is skipped when it is a story POI, within `storyClearM` beyond
 * a story site's radius in this system, restorable (M7 can restart it), within `dockClearM` of a restorable derelict
 * (its docking volume, the salvage site's included), within `minSpacingM` of a cuttable derelict, or shares a seed
 * with one. Deterministic per galaxy seed; `main` is never changed.
 */
export function graveyardSalvageSites(
  layout: GraveyardLayout,
  main: SalvageSite | null,
  ctx: GraveyardSiteContext,
  cfg: GraveyardSitesConfig,
  reactivation: ReactivationConfig = storyData().reactivation,
): SalvageSite[] {
  if (layout.archetype !== 'derelict-graveyard' || main === null || main.rush === 'sealed' || layout.home !== null) return []
  const story = ctx.story
  if (story === null || cfg.extraSites === 0) return []
  const galaxy = { seed: ctx.galaxySeed }
  const system = { id: layout.systemId, index: layout.systemIndex, region: layout.region, archetype: layout.archetype }
  const lab = { okaforLabPoiId: story.okaforLab.poiId }
  const derelicts = layout.pois.filter((p) => p.kind === 'derelict')
  const restorable = derelicts.filter((p) => frStationOf(galaxy, system, p, reactivation, lab).restorable)
  const storySpots = [story.columnNine, story.okaforLab].filter((x) => x.systemId === layout.systemId)
  const mainAt = [main.pose.position.x, main.pose.position.y, main.pose.position.z]
  const taken: { readonly at: readonly number[]; readonly seed: number }[] = [{ at: mainAt, seed: main.seed >>> 0 }]
  const out: SalvageSite[] = []
  for (const poi of derelicts) {
    if (out.length >= cfg.extraSites) break
    if (poi.id === main.poiId || poi.id === story.okaforLab.poiId || poi.id === story.columnNine.poiId) continue
    if (storySpots.some((x) => distance(poi.position, x.position) < x.radiusM + cfg.storyClearM)) continue
    if (restorable.some((r) => r.id === poi.id || distance(poi.position, r.position) < cfg.dockClearM)) continue
    if (taken.some((t) => t.seed === poi.seed >>> 0 || distance(poi.position, t.at) < cfg.minSpacingM)) continue
    taken.push({ at: poi.position, seed: poi.seed >>> 0 })
    out.push(siteAt(poi, layout.region, 'graveyard'))
  }
  return out
}

/**
 * Puts M2's derelict sections on `site` (none when null), after setting the site's region for their Mk roll; the
 * site's Rush kind goes with them (M8 MC-h1). `extras` are the system's extra graveyard sites (M8 MC-h2).
 */
function placeSalvageSite(target: SystemWorldTarget, site: SalvageSite | null, extras: readonly SalvageSite[]): void {
  if (site === null) {
    target.space.salvage.setDerelict(null, 0)
    return
  }
  target.space.salvage.setRegion(site.region)
  if (extras.length === 0) {
    target.space.salvage.setDerelict(site.pose, site.seed, site.rush)
    return
  }
  const spots: DerelictSpot[] = extras.map((e) => ({ pose: e.pose, seed: e.seed, site: e.rush }))
  target.space.salvage.setDerelict(site.pose, site.seed, site.rush, spots)
}

/** What `switchSystemWorld` did. */
export type SystemWorldSwitch = 'first' | 'same' | 'switched'

/**
 * Entering `systemId`: moves M2's world of the watched system into its deltas and imports the entered system's
 * wrecks, and puts M2's derelict on the entered system's salvage `site` (every entry; see the header), with the
 * system's extra graveyard sites (`extras`, M8 MC-h2). The watch then names the entered system, with no field until
 * the next watch.
 */
export function switchSystemWorld(
  watch: SystemWorldWatch,
  target: SystemWorldTarget,
  state: ReachState,
  systemId: string,
  site: SalvageSite | null,
  extras: readonly SalvageSite[] = NO_SITES,
): SystemWorldSwitch {
  const from = watch.systemId
  const field = watch.field
  const keys = watch.keys
  const mixAt = watch.mixAt
  const sameCareer = watch.deltas === state.deltas
  watch.systemId = systemId
  watch.field = null
  watch.keys = NO_KEYS
  watch.deltas = state.deltas
  watch.mixAt = null
  if (from === null || !sameCareer || from === systemId) {
    placeSalvageSite(target, site, extras)
    return from === null || !sameCareer ? 'first' : 'same'
  }

  const rocks = leavingRockDeltas(target.space.rocks.exportDeltas())
  target.space.rocks.importDeltas(rocks)
  writeRockFractions(rocks, field, keys, target.data.mining.massPerCubicMetre, state, from, mixAt === null ? null : { mixAt, mix: target.data.mining.mix })

  const salvage = target.space.salvage.exportDeltas()
  if (salvage.wrecks.length > 0 || state.deltas[from] !== undefined) ensureDelta(state, from).wrecks = salvage.wrecks
  placeSalvageSite(target, site, extras)
  target.space.salvage.importDeltas({ nextSeq: salvage.nextSeq, wrecks: storedWrecks(state, systemId) })
  logger.debug(`bridge: M2's world left ${from} (${salvage.wrecks.length} wrecks) for ${systemId}`)
  return 'switched'
}

/** Strategic hull id → its `ships.json` def id (`hulls.json shipDef`; a hulk's `hull` is a strategic id). */
const HULK_SHIP_DEF: ReadonlyMap<string, string> = new Map(hullsJson.hulls.map((h) => [h.id, h.shipDef]))

/** The `ships.json` def a hulk of strategic hull `hull` spawns as, or null for an unknown hull. */
export function hulkShipDefOf(hull: string): string | null {
  return HULK_SHIP_DEF.get(hull) ?? null
}

/**
 * The seeded hulks to spawn on entering `layout`'s system (M9 CH-2c): `hulksOf` in key order, without the career's
 * finished keys (`done`, from `career.hulks.done`: a claimed or scrapped hulk never comes back), at most `maxLocal`
 * without an open claim; a hulk whose key is in `open` (the Career's open claims) always comes and doesn't count
 * (PG-2 R2-4: the salvage world holds open claims' hulks outside `hulks.maxLocal`).
 * None without the story overlay (`sites` null: placement needs it to keep clear of the story sites). Home, the gate
 * region and the story sites' systems (the sealed ones) place none (`hulks.json`). `table` is the galaxy's hulk placement
 * table (M10 P3b1; the caller passes `hulkTableOf(career)`, P3b2). Event-driven; it allocates.
 */
export function systemHulks(
  galaxy: Galaxy,
  layout: SystemLayout,
  sites: StorySites | null,
  table: number,
  done: readonly string[] | undefined,
  maxLocal: number,
  open: readonly string[] = [],
  cfg: HulksConfig = hulksConfig(),
): HulkSpot[] {
  if (sites === null) return []
  const out: HulkSpot[] = []
  let unclaimed = 0
  for (const h of hulksOf(galaxy, layout, sites, table, cfg)) {
    if (done !== undefined && done.includes(h.key)) continue
    if (open.includes(h.key)) out.push(h)
    else if (unclaimed < maxLocal) {
      out.push(h)
      unclaimed++
    }
  }
  return out
}

/** The keys of the Career's open hulk claims (`career.hulks.sites` with `open`; PG-2 R2-4). Event-driven; it allocates. */
export function openHulkKeys(sites: Readonly<Record<string, { readonly open?: true }>> | undefined): string[] {
  if (sites === undefined) return []
  return Object.keys(sites).filter((k) => sites[k].open === true)
}

const hulkPos: Vec3 = { x: 0, y: 0, z: 0 }
const hulkRot: Quat = { x: 0, y: 0, z: 0, w: 1 }

/**
 * Spawns `hulks` as `hulk` wrecks at their system-frame poses (the salvage world's frame, like the derelict POIs';
 * D-013 rebasing is the renderer's), each keyed by its `hk:` key; `defIndexOf` maps a `ships.json` def id to the sim's
 * def index (-1 unknown); a key in `open` (an open claim) spawns claimed (PG-2 R2-4). A hulk of an unknown hull or def,
 * or one the salvage world refuses (no room, a full pool), is skipped. Returns how many are alive afterwards (an already alive one counts: `spawnHulk` is idempotent).
 */
export function spawnSystemHulks(
  salvage: Pick<SalvageRuntime, 'spawnHulk'>,
  hulks: readonly HulkSpot[],
  defIndexOf: (shipDefId: string) => number,
  open: readonly string[] = [],
): number {
  let n = 0
  for (let i = 0; i < hulks.length; i++) {
    const h = hulks[i]
    const defId = hulkShipDefOf(h.hull)
    const def = defId === null ? -1 : defIndexOf(defId)
    if (def < 0) {
      logger.warn(`bridge: hulk ${h.key}: hull ${h.hull} has no ship def, not spawned`)
      continue
    }
    hulkPos.x = h.position[0]
    hulkPos.y = h.position[1]
    hulkPos.z = h.position[2]
    hulkRot.x = h.rotation[0]
    hulkRot.y = h.rotation[1]
    hulkRot.z = h.rotation[2]
    hulkRot.w = h.rotation[3]
    if (salvage.spawnHulk(def, hulkPos, hulkRot, h.key, open.includes(h.key)) >= 0) n++
  }
  return n
}
